import Page from "components/Page"
import MediaCard from "components/MediaCard"
import {
  getMediaEpisode,
  getMediaEpisodesBySection,
  getMediaShow,
} from "data/media"
import { Link, useParams } from "react-router-dom"
import usePageMeta from "hooks/usePageMeta"
import { getYoutubeThumbnailUrl } from "utils/socialPreview"

const MediaDetailPage = () => {
  const { slug } = useParams()
  const episode = getMediaEpisode(slug)
  usePageMeta(
    episode ? `${episode.title} — r4v3n` : undefined,
    episode?.description,
    getYoutubeThumbnailUrl(episode?.youtubeId),
    episode ? `/media/${episode.slug}` : undefined,
  )

  if (!episode) {
    return (
      <Page>
        <div className="gallery-empty">
          Media episode not found. <Link to="/media" className="underline">Back to Media</Link>
        </div>
      </Page>
    )
  }

  const show = getMediaShow(episode.section)
  const formatLabel = episode.format === "video" ? "Video" : "Audio"
  const related = getMediaEpisodesBySection(episode.section)
    .filter((item) => item.slug !== episode.slug)
    .slice(0, 3)

  return (
    <Page>
      <div className="mb-8">
        <Link to="/media" className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors">
          Back to Media
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.32fr_0.68fr] gap-8 md:gap-10 items-start">
        <div className="border border-gallery-border bg-gallery-surface p-3 md:p-4">
          <div className="aspect-video w-full bg-gallery-subtle">
            {episode.format === "video" && episode.youtubeId ? (
              <iframe
                src={`https://www.youtube.com/embed/${episode.youtubeId}`}
                title={episode.title}
                className="h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center px-6 text-center">
                <div>
                  <p className="gallery-label text-gallery-muted">Audio episode</p>
                  <a
                    href={episode.externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gallery-btn mt-4"
                  >
                    Listen externally
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

        <aside>
          <p className="gallery-label text-gallery-muted">{episode.seriesTitle}</p>
          <h1 className="mt-3 font-serif text-4xl md:text-5xl leading-tight font-normal">
            {episode.title}
          </h1>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <div className="gallery-stat">
              <span className="text-xl">{formatLabel}</span>
              <p>Format</p>
            </div>
            <div className="gallery-stat">
              <span className="text-xl">{episode.duration || episode.date}</span>
              <p>{episode.duration ? "Duration" : "Episode"}</p>
            </div>
          </div>
          <p className="mt-6 text-sm leading-relaxed text-gallery-text">
            {episode.description}
          </p>
          <dl className="mt-6 space-y-3 text-sm">
            <div>
              <dt className="gallery-label text-gallery-muted">Guests</dt>
              <dd className="mt-1 text-gallery-text">{episode.guest}</dd>
            </div>
            <div>
              <dt className="gallery-label text-gallery-muted">Date</dt>
              <dd className="mt-1 text-gallery-text">{episode.date}</dd>
            </div>
          </dl>
          <div className="mt-7 flex flex-wrap gap-2">
            <a
              href={episode.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="gallery-btn-primary text-xs px-4 py-2"
            >
              Open source
            </a>
            {show?.links.map((link) => (
              <a
                key={link.url}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="gallery-btn text-xs px-4 py-2"
              >
                {link.label}
              </a>
            ))}
          </div>
        </aside>
      </div>

      {related.length > 0 && (
        <section className="mt-16 md:mt-20">
          <div className="mb-6 md:mb-8">
            <p className="gallery-label text-gallery-muted">Keep watching</p>
            <h2 className="gallery-subheading mt-2">Related episodes</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {related.map((item) => (
              <MediaCard key={item.slug} episode={item} compact />
            ))}
          </div>
        </section>
      )}
    </Page>
  )
}

export default MediaDetailPage
